import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Wand2, Settings, Briefcase, Users, Calendar, Target, ArrowRight,
  Cpu, Award, Gift, KeyRound, Building2, Unlock,
} from 'lucide-react';
import Nav from '../components/nav';
import SEO from '../components/SEO';
import studentProgramBanner from '../assets/img/student-program2.jpg';

const BENEFITS = [
  { icon: Wand2, title: '2 months free', description: 'One AI tool, on us' },
  { icon: Settings, title: 'Academy pricing', description: 'Discounted plans after' },
  { icon: Briefcase, title: 'Portfolio', description: 'Build your showreel' },
  { icon: Users, title: 'Community & alumni', description: 'Stay connected after' },
  { icon: Calendar, title: 'Workshops & challenges', description: 'Learn and compete' },
  { icon: Target, title: 'Opportunities', description: 'Get discovered' },
];

const INSTITUTE_VALUE_PROPS = [
  { icon: Cpu, title: 'Industry-standard AI tools', description: 'The same script, budget, and production tools working studios use — not a stripped-down classroom version.' },
  { icon: Award, title: 'A portfolio, not just a grade', description: 'Students graduate with a completed showreel and produced work, not just a course completion certificate.' },
  { icon: Gift, title: 'No cost to the institution', description: 'No licensing fees, no procurement process, no budget line to justify. The free period is funded for your students, not billed to you.' },
  { icon: KeyRound, title: 'Rollout in one code', description: 'One program code covers your whole cohort. Students self-serve their own signup — no IT rollout, no seat management.' },
];

const HOW_IT_WORKS = [
  { icon: Building2, step: '1', title: 'Institute partners', description: 'Your institute signs up and receives a unique student program code.' },
  { icon: KeyRound, step: '2', title: 'Students get the code', description: 'Share the code with your students. They enter it once, during their own signup.' },
  { icon: Unlock, step: '3', title: 'Tools unlock', description: 'Tools unlock as soon as their program code and details are confirmed.' },
];

const StudentProgram = () => {
  const navigate = useNavigate();

  return (
    <>
      <SEO
        title="Student Program"
        description="ProducerBuddy's Student Program gives students at partnered institutes two months of free AI filmmaking tools, plus a portfolio, community, and ongoing academy pricing."
        url="https://producerbuddy.pilomation.com/student-program"
        robots="index, follow"
      />
      <Nav />
      <div className="min-h-screen bg-gray-900 text-white overflow-hidden">
        {/* Decorative banner */}
        <img src={studentProgramBanner} alt="" className="w-full h-auto" />

        {/* Hero */}
        <section className="relative max-w-5xl mx-auto px-6 pt-24 pb-20 text-center">
          {/* Soft teal glow behind the headline */}
          <div
            className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[36rem] h-[36rem] rounded-full bg-teal-600/20 blur-3xl"
            aria-hidden="true"
          />

          <div className="relative">
            <span className="inline-block text-teal-400 text-sm font-semibold tracking-wide uppercase mb-4">
              ProducerBuddy Student Program
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Free AI filmmaking tools, built for film students
            </h1>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto leading-relaxed">
              Two months of free AI filmmaking tools for students at partnered institutes, plus a
              portfolio, community, and academy pricing that continues after. Whether you're
              breaking down your first script or assembling a reel for festival submissions, the
              Student Program gives you the same tools working productions rely on — without the
              cost of figuring it all out alone. Learn the craft, build a body of work, and stay
              connected to a community of filmmakers long after the free period ends.
            </p>
          </div>
        </section>

        {/* Divider between hero and grid */}
        <div className="max-w-5xl mx-auto px-6">
          <div className="h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent" />
        </div>

        {/* Benefit grid */}
        <section className="max-w-5xl mx-auto px-6 pt-16 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {BENEFITS.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="group bg-gray-800 border border-gray-700 rounded-2xl p-6 transition-all duration-300 hover:border-teal-600 hover:-translate-y-1 hover:shadow-xl hover:shadow-teal-600/10"
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-teal-600/20 text-teal-400 mb-4 transition-transform duration-300 group-hover:scale-110">
                  <Icon size={24} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
                <p className="text-gray-400 text-sm">{description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why partner with ProducerBuddy */}
        <section className="max-w-5xl mx-auto px-6 pb-20">
          <div className="text-center mb-10">
            <span className="inline-block text-teal-400 text-sm font-semibold tracking-wide uppercase mb-4">
              For institutions
            </span>
            <h2 className="text-3xl font-bold text-white">Why partner with ProducerBuddy</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {INSTITUTE_VALUE_PROPS.map(({ icon: Icon, title, description }) => (
              <div key={title} className="flex gap-4 bg-gray-800 border border-gray-700 rounded-2xl p-6">
                <div className="w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center bg-teal-600/20 text-teal-400">
                  <Icon size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
                  <p className="text-gray-400 text-sm">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* How it works */}
        <section className="max-w-5xl mx-auto px-6 pb-20">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white">How it works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {HOW_IT_WORKS.map(({ icon: Icon, step, title, description }, i) => (
              <div key={step} className="relative text-center">
                <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center bg-teal-600/20 text-teal-400 mb-4">
                  <Icon size={26} />
                </div>
                <span className="text-teal-400 text-xs font-semibold tracking-wide uppercase">Step {step}</span>
                <h3 className="text-lg font-semibold text-white mt-1 mb-1">{title}</h3>
                <p className="text-gray-400 text-sm">{description}</p>
                {i < HOW_IT_WORKS.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-5 -right-3 text-gray-600" size={20} />
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Two-panel split */}
        <section className="max-w-5xl mx-auto px-6 pb-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Students panel — primary path, glow-lifted */}
            <div className="relative bg-gray-800 border-2 border-teal-600 rounded-2xl p-8 shadow-lg shadow-teal-600/20 transition-shadow duration-300 hover:shadow-teal-600/30">
              <span className="inline-block bg-teal-600/20 text-teal-400 text-xs font-semibold tracking-wide uppercase px-3 py-1 rounded-full mb-4">
                For students
              </span>
              <h2 className="text-2xl font-bold text-white mb-2">Have a student program code?</h2>
              <p className="text-gray-400 mb-6">Sign up and enter it during onboarding.</p>
              <button
                onClick={() => navigate('/student-program/signup')}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-gray-900 bg-white hover:bg-gray-100 transition-all"
              >
                Create your account <ArrowRight size={18} />
              </button>
            </div>

            {/* Institutes panel — secondary, neutral */}
            <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 transition-colors duration-300 hover:border-gray-600">
              <span className="inline-block bg-gray-700 text-gray-300 text-xs font-semibold tracking-wide uppercase px-3 py-1 rounded-full mb-4">
                For institutes
              </span>
              <h2 className="text-2xl font-bold text-white mb-2">Representing a film school or university?</h2>
              <p className="text-gray-400 mb-6">Talk to us about a partnership.</p>
              <a
                href="https://www.pilomation.com/producerbuddy/book-demo"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold text-white border-2 border-teal-600 hover:bg-teal-600/10 transition-all"
              >
                Book a demo <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default StudentProgram;
